import { useTranslation } from 'react-i18next';
import { SortOrder } from 'utils/constants';
import { commonDescendingComparator } from 'utils/helper';

const applySort = (order, data, getter) => {
    if (!order) return data;
    if (order === SortOrder.DESC) {
        return [...data].sort((a, b) => commonDescendingComparator(a, b, getter));
    }
    return [...data].sort((a, b) => -commonDescendingComparator(a, b, getter));
};

export const useGraderProgressTableColumns = () => {
    const { t } = useTranslation();
    return [
        {
            id: 'gradedBy',
            columnTitle: t('common:table.graded-by', 'Graded By'),
            comparator: (order, graders) => {
                if (!order) return graders;
                const getter = (grader) => grader.name;
                return applySort(order, graders, getter);
            },
        },
        {
            id: 'submissionsGraded',
            columnTitle: t('common:table.submissions-graded', 'Submissions Graded'),
            comparator: (order, graders) => {
                if (!order) return graders;
                const getter = (grader) => grader.submissionsGraded;
                return applySort(order, graders, getter);
            },
        },
        {
            id: 'avgGrade',
            columnTitle: t('common:table.avg-grade-given', 'Avg Grade Given'),
            comparator: (order, graders) => {
                if (!order) return graders;
                const getter = (grader) => grader.rawAvgGrade;
                return applySort(order, graders, getter);
            },
        },
        {
            id: 'totalTime',
            columnTitle: t('common:table.total-grading-time-hhmm', 'Total Grading Time (hh:mm)'),
            comparator: (order, graders) => {
                if (!order) return graders;
                const getter = (grader) => grader.rawTime;
                return applySort(order, graders, getter);
            },
        },
        {
            id: 'avgTime',
            columnTitle: t('common:table.avg-grading-time-mmss', 'Avg Grading Time (mm:ss)'),
            comparator: (order, graders) => {
                if (!order) return graders;
                const getter = (grader) => grader.rawAvgTime;
                return applySort(order, graders, getter);
            },
        },
        {
            id: 'aiAccuracy',
            columnTitle: t('common:table.ai-accuracy', 'AI Accuracy'),
            comparator: (order, graders) => {
                if (!order) return graders;
                const getter = (grader) => grader.rawAiAccuracy;
                return applySort(order, graders, getter);
            },
        },
    ];
};

export const useStudentStatisticsTableColumns = () => {
    const { t } = useTranslation();
    return [
        {
            id: 'name',
            key: 'name',
            columnTitle: t('common:table.file-name', 'File Name'),
            comparator: (order, rows) => {
                if (!order) return rows;
                const getter = (row) => row.name;
                return applySort(order, rows, getter);
            },
        },
        {
            id: 'manual',
            key: 'manual',
            rowKey: 'manualGradeText',
            columnTitle: t('common:table.manual-grading', 'Manual Grading'),
            comparator: (order, rows) => {
                if (!order) return rows;
                const getter = (row) => row.manualRate;
                return applySort(order, rows, getter);
            },
        },
        {
            id: 'ai',
            key: 'ai',
            rowKey: 'aiGradeText',
            columnTitle: t('common:table.ai-grading', 'AI Grading'),
            comparator: (order, rows) => {
                if (!order) return rows;
                const getter = (row) => row.aiRate;
                return applySort(order, rows, getter);
            },
        },
        {
            id: 'accuracy',
            key: 'accuracy',
            rowKey: 'accuracyText',
            columnTitle: t('common:table.ai-accuracy', 'AI Accuracy'),
            comparator: (order, rows) => {
                if (!order) return rows;
                const getter = (row) => row.accuracy;
                return applySort(order, rows, getter);
            },
        },
    ];
};
